import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppService } from '../../../services/app.service';
import { UtilsService } from '../../../services/utils.service';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/users.service';
import { VirtualCardsService } from '../../../services/virtual_cards.service';
import { BitgoService } from '../../../services/bitgo.service';

const VIRTUAL_CARD_CREATE_CURRENCY = 'USDT';

@Component({
  selector: 'app-add-virtual-card',
  templateUrl: './add-virtual-card.component.html',
  styleUrl: './add-virtual-card.component.scss',
})
export class AddVirtualCardComponent implements OnInit {
  showAddCard = false;
  addingCard = false;
  wallets: Array<{
    id: string;
    user_subwallet_id?: number | null;
    shortname: string;
    balance: number;
    wallet_shortname: string;
  }> = [];
  loadingWallets = false;
  createFeeAmount: number | null = null;
  createFeeCurrency = VIRTUAL_CARD_CREATE_CURRENCY;
  loadingCreateFee = false;
  createFeeError = '';
  newCardForm = this.formBuilder.group({
    alias: ['', Validators.required],
    card_holder_name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone_country_code: [null, Validators.required],
    phone: [null, Validators.required],
    daily_limit: [null],
    weekly_limit: [null],
    monthly_limit: [null],
    wallet_id: [null as any, Validators.required],
  });
  constructor(
    private cardsService: VirtualCardsService,
    private formBuilder: FormBuilder,
    public utilsService: UtilsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public auth: AuthService,
    public appService: AppService,
    public userService: UserService,
    private bitgoService: BitgoService,
  ) {}

  ngOnInit() {
    if (!this.userService.hasPlatformService('virtual-card:create')) {
      this.appService.triggerAddVirtualCard(false);
      return;
    }

    this.showAddCardDialog();
  }

  getUsdtWallets() {
    const subwallets =
      this.userService.user_balance_config?.currencies?.find(
        (currency: any) => String(currency?.id || '').toUpperCase() === 'USDT',
      )?.subwallets ?? [];

    return subwallets
      .filter((wallet: any) => wallet?.id)
      .map((wallet: any, index: number) => {
        const balance = Number(wallet.balance || 0);
        const shortname = wallet.shortname || `Wallet ${index + 1}`;
        return {
          id: String(wallet.id),
          shortname,
          balance,
          wallet_shortname: `${shortname} — ${balance} USDT`,
        };
      });
  }

  selectedWalletLabel(): string {
    const wallet = this.newCardForm.controls.wallet_id.value;
    return wallet?.wallet_shortname || wallet?.shortname || '';
  }

  formattedCreateFee(): string {
    if (this.createFeeAmount === null) return '';
    return `${this.createFeeAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    })} ${this.createFeeCurrency}`;
  }

  canSubmitCard(): boolean {
    return (
      this.newCardForm.valid &&
      !this.addingCard &&
      !this.loadingCreateFee &&
      !this.createFeeError &&
      this.createFeeAmount !== null &&
      this.createFeeAmount >= 0
    );
  }

  exitNewCard() {
    this.newCardForm.reset();
    this.showAddCard = false;
    this.appService.triggerAddVirtualCard(false);
  }

  confirmSaveCard(event: Event) {
    if (this.addingCard || !this.canSubmitCard()) return;

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `
        Importante: La creación de una tarjeta virtual genera un cargo<br>
        automático de <b>${this.formattedCreateFee()}</b> desde el wallet seleccionado
        (${this.selectedWalletLabel() || 'sin wallet'}).
        <br><br>
        Si no se cuenta con fondos suficientes, la tarjeta no podrá ser creada.
        <br><br>
        ¿Estás seguro de querer agregar una tarjeta nueva?
      `,
      header: 'Confirmación',
      icon: undefined,
      acceptIcon: 'none',
      acceptLabel: 'Si, agregar.',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.addingCard = true;
        this.saveCard();
      },
    });
  }

  saveCard() {
    if (!this.auth.guardTransactionalAction()) return;
    if (!this.userService.hasPlatformService('virtual-card:create')) return;

    const sessionUserId = this.auth.user?.user_id ?? null;

    const selectedWallet = this.newCardForm.controls.wallet_id.value;
    let body = {
      alias: this.newCardForm.controls.alias.value,
      card_holder_name: this.newCardForm.controls.card_holder_name.value,
      email: this.newCardForm.controls.email.value,
      phone_country_code: this.newCardForm.controls.phone_country_code.value,
      phone: this.newCardForm.controls.phone.value,
      daily_limit: this.newCardForm.controls.daily_limit.value,
      weekly_limit: this.newCardForm.controls.weekly_limit.value,
      monthly_limit: this.newCardForm.controls.monthly_limit.value,
      approval_method: 'TOP_UP',
      // Titular de la tarjeta: user_id explícito; el backend cae a la sesión si falta.
      user_id: sessionUserId,
      ...(selectedWallet?.user_subwallet_id
        ? { user_subwallet_id: selectedWallet.user_subwallet_id }
        : { wallet_id: selectedWallet?.id ?? selectedWallet }),
    };

    this.cardsService.postCard(body).then(
      (resp: any) => {
        this.addingCard = false;
        this.appService.triggerRefreshVirtualCards();
        this.appService.triggerAddVirtualCard(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Hemos añadido una nueva tarjerta a tu cuenta.',
        });
      },
      (resp: any) => {
        const code = String(resp?.code || '');
        const backendMessage = String(resp?.error || resp?.message || '');
        const message =
          code === 'VIRTUAL_CARD_INSUFFICIENT_BALANCE' ||
          backendMessage.includes('Insufficient')
            ? 'Saldo de USDT insuficiente en el wallet seleccionado para crear la tarjeta.'
            : code === 'VIRTUAL_CARD_WALLET_NOT_FOUND' ||
                backendMessage.includes('wallet_id')
              ? 'El wallet seleccionado no es válido para cobrar el fee de creación.'
              : 'Ocurrió un error al agregar tu nueva tarjeta.';

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
        this.addingCard = false;
      },
    );
  }

  showAddCardDialog() {
    this.newCardForm.reset();
    this.showAddCard = true;
    this.loadCreateFee();
    this.loadChargeWallets();
  }

  private loadCreateFee() {
    this.loadingCreateFee = true;
    this.createFeeError = '';
    this.createFeeAmount = null;
    this.createFeeCurrency = VIRTUAL_CARD_CREATE_CURRENCY;

    this.cardsService.getCreateFee(this.auth.user?.user_id ?? null).then(
      (response: any) => {
        const data = response?.data ?? response;
        const amount = this.toFeeNumber(data?.amount);
        if (!Number.isFinite(amount) || amount < 0) {
          this.createFeeError =
            'No hay un fee de creación de tarjeta configurado (virtual_card_create).';
          this.loadingCreateFee = false;
          return;
        }

        this.createFeeAmount = amount;
        this.createFeeCurrency =
          String(data?.currency || VIRTUAL_CARD_CREATE_CURRENCY)
            .trim()
            .toUpperCase() || VIRTUAL_CARD_CREATE_CURRENCY;
        this.loadingCreateFee = false;
      },
      (error: any) => {
        this.createFeeError =
          error?.error ||
          error?.message ||
          'No se pudo consultar el fee de creación de la tarjeta.';
        this.loadingCreateFee = false;
      },
    );
  }

  private toFeeNumber(value: number | string | null | undefined): number {
    if (value === null || value === undefined || value === '') return NaN;
    const numericValue = Number(String(value).replace(',', '.'));
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  private loadChargeWallets() {
    const sessionUserId = this.auth.user?.user_id;
    this.loadingWallets = true;
    this.bitgoService
      .listWallets(sessionUserId, { tree: false, includeBalances: true })
      .subscribe(
        (res) => {
          this.loadingWallets = false;
          const data = res?.data ?? res;
          const cryptoWallets = this.mapCryptoWallets(data);
          this.wallets = cryptoWallets.length
            ? cryptoWallets
            : this.getUsdtWallets();
        },
        () => {
          this.loadingWallets = false;
          this.wallets = this.getUsdtWallets();
        },
      );
  }

  private mapCryptoWallets(data: any) {
    const rows = Array.isArray(data?.wallets)
      ? data.wallets
      : Array.isArray(data?.roots)
        ? data.roots
        : Array.isArray(data)
          ? data
          : [];
    const usdtNetworks = new Set(['polygon', 'tron', 'ofc']);
    return rows
      .filter((wallet: any) => {
        const coin = String(wallet?.coin || '').toUpperCase();
        const network = String(wallet?.network || '').toLowerCase();
        return (
          coin === 'USDT' ||
          coin === 'USDC' ||
          usdtNetworks.has(network)
        );
      })
      .map((wallet: any) => {
        const label =
          wallet.label ||
          wallet.network ||
          wallet.bitgo_wallet_id ||
          `Wallet ${wallet.user_subwallet_id}`;
        const balance = Number(
          wallet.ledger_balance ?? wallet.balance ?? 0,
        );
        return {
          id: String(wallet.bitgo_wallet_id || wallet.subwallet_id || wallet.user_subwallet_id),
          user_subwallet_id: wallet.user_subwallet_id ?? null,
          shortname: label,
          balance,
          wallet_shortname: `${label} — ${balance} USDT`,
        };
      });
  }
}
